import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, GraduationCap, Users, Building2, Baby, BookOpen, Award, Briefcase, Phone, Mail, Info, FileQuestion, LogIn, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cmsAPI, settingsAPI, analyticsAPI } from '../../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

// Images pour le megamenu
const MEGAMENU_IMAGES = {
  youngLearners: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/8l5pl8n1_2025-06-18.webp',
  adolescents: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/6p8f6v3h_2022-09-25.webp',
  adultes: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/sir01hxa_2021-09-05.webp',
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null); // 'enfants' ou 'formations'
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [headerData, setHeaderData] = useState(null);
  const [settings, setSettings] = useState({});
  const menuTimeoutRef = React.useRef(null);
  const headerRef = React.useRef(null);
  const [menuTop, setMenuTop] = useState(80);

  // Mesure dynamiquement le bas du header pour positionner les megamenus juste en dessous,
  // même quand le bandeau d'inscription est affiché ou que la page est scrollée.
  const updateMenuTop = React.useCallback(() => {
    if (headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setMenuTop(Math.max(0, rect.bottom));
    }
  }, []);

  useEffect(() => {
    updateMenuTop();
    window.addEventListener('scroll', updateMenuTop, { passive: true });
    window.addEventListener('resize', updateMenuTop);
    // ResizeObserver pour détecter l'apparition/disparition du bandeau d'inscription
    let observer = null;
    if (headerRef.current && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateMenuTop());
      observer.observe(headerRef.current);
    }
    return () => {
      window.removeEventListener('scroll', updateMenuTop);
      window.removeEventListener('resize', updateMenuTop);
      if (observer) observer.disconnect();
    };
  }, [updateMenuTop]);

  // Fonction pour ouvrir un menu spécifique
  const openMenu = (menuType) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    updateMenuTop();
    setActiveMenu(menuType);
  };

  // Fonction pour fermer le menu avec délai
  const closeMenuWithDelay = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 300);
  };

  useEffect(() => {
    loadHeaderData();
  }, []);

  const loadHeaderData = async () => {
    try {
      const [sectionsRes, settingsRes] = await Promise.all([
        cmsAPI.getSections('global'),
        settingsAPI.get()
      ]);
      const header = sectionsRes.data.find(s => s.section_key === 'header');
      setHeaderData(header);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading header data:', error);
    }
  };

  // Track clicks on phone/email buttons
  const trackClick = async (type) => {
    try {
      if (analyticsAPI && analyticsAPI.trackClick) {
        await analyticsAPI.trackClick(type);
      }
    } catch (error) {
      console.log('Analytics not available');
    }
  };

  const logoUrl = settings.logo_url || headerData?.image_url || 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

  // Fallback logo component when no image URL is available
  const LogoFallback = () => (
    <div className="flex items-center">
      <div className="bg-[#285BBB] rounded-lg p-2 mr-2">
        <GraduationCap className="h-6 w-6 text-white" />
      </div>
      <div className="hidden sm:block">
        <div className="text-[#285BBB] font-bold text-sm leading-tight">THE ENGLISH</div>
        <div className="text-[#2C3C65] font-bold text-sm leading-tight">SPEAKING CENTER</div>
      </div>
      <span className="sm:hidden text-[#285BBB] font-bold">TESC</span>
    </div>
  );

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Le Centre', path: '/#centre' },
    { 
      name: 'Cours d\'anglais', 
      path: '#',
      isMegaMenu: true,
      menuType: 'enfants'
    },
    { 
      name: 'Nos Formations', 
      path: '#',
      isMegaMenu: true,
      menuType: 'formations'
    },
    { name: 'Cambridge', path: '/cambridge' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  // Données du megamenu - Cours d'anglais (par niveaux CECRL)
  const coursAnglaisData = [
    {
      category: 'Young Learners',
      subtitle: 'Pre A1 → A2',
      icon: Baby,
      color: 'bg-green-500',
      lightColor: 'bg-green-50',
      image: MEGAMENU_IMAGES.youngLearners,
      link: '/programmes/enfants',
      description: 'Enfants 7-12 ans',
      programs: [
        { name: 'Pre A1 Starters', level: 'Pre A1', desc: 'Découverte' },
        { name: 'A1 Movers', level: 'A1', desc: 'Progression' },
        { name: 'A2 Flyers', level: 'A2', desc: 'Consolidation' },
      ]
    },
    {
      category: 'For Schools',
      subtitle: 'A2 → B2',
      icon: Users,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      image: MEGAMENU_IMAGES.adolescents,
      link: '/programmes/adolescents',
      description: 'Adolescents 12-18 ans',
      programs: [
        { name: 'A2 Key for Schools', level: 'A2', desc: 'Fondamentaux' },
        { name: 'B1 Preliminary for Schools', level: 'B1', desc: 'Intermédiaire' },
        { name: 'B2 First for Schools', level: 'B2', desc: 'Avancé' },
      ]
    },
    {
      category: 'Advanced & Proficiency',
      subtitle: 'C1 → C2',
      icon: Award,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      image: MEGAMENU_IMAGES.adultes,
      link: '/cambridge',
      description: 'Niveaux experts',
      programs: [
        { name: 'C1 Advanced (CAE)', level: 'C1', desc: 'Niveau avancé' },
        { name: 'C2 Proficiency (CPE)', level: 'C2', desc: 'Maîtrise' },
      ]
    },
  ];

  // Données du megamenu - Nos Formations (CPF & Entreprises)
  const formationsData = [
    {
      category: 'Formations CPF',
      subtitle: 'Adultes - 100% éligible',
      icon: Briefcase,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      image: MEGAMENU_IMAGES.adultes,
      link: '/programmes/adultes',
      description: 'LEVELTEL Certifiant',
      programs: [
        { name: 'Formation individuelle', level: 'CPF', desc: 'Sur mesure' },
        { name: 'LEVELTEL Certifiant', level: 'A2+', desc: 'Certification' },
        { name: 'Linguaskill', level: 'Multi', desc: 'Cambridge' },
      ]
    },
    {
      category: 'Entreprises',
      subtitle: 'Sur mesure',
      icon: Building2,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-50',
      image: MEGAMENU_IMAGES.adultes,
      link: '/devis',
      description: 'Formations professionnelles',
      programs: [
        { name: 'Formation intra', level: 'OPCO', desc: 'Dans vos locaux' },
        { name: 'Formation inter', level: 'CPF', desc: 'À notre centre' },
        { name: 'Anglais métier', level: 'Pro', desc: 'Spécialisation' },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header ref={headerRef} className="bg-white shadow-md sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={logoUrl} 
              alt="The English Speaking Center" 
              className="h-12 lg:h-14 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center">
              <LogoFallback />
            </div>
          </Link>

          {/* Mobile Quick Actions */}
          <div className="flex lg:hidden items-center gap-1">
            {/* Phone Button - Blue */}
            <a 
              href="tel:+33626716022"
              onClick={() => trackClick('phone')}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#285BBB] text-white"
              aria-label="Appeler"
            >
              <Phone className="h-4 w-4" />
            </a>
            {/* Email Button - Blue */}
            <a 
              href="mailto:contact@theenglishspeakingcenter.fr"
              onClick={() => trackClick('email')}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#285BBB] text-white"
              aria-label="Email"
            >
              <Mail className="h-4 w-4" />
            </a>
            {/* Login Button - Red with white user icon */}
            <Link 
              to="/login"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#ff3a2d] text-white"
              aria-label="Connexion"
            >
              <User className="h-4 w-4" />
            </Link>
            {/* Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-[#2C3C65]"
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-0">
            {navLinks.map((link) => (
              link.isMegaMenu ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => openMenu(link.menuType)}
                  onMouseLeave={closeMenuWithDelay}
                >
                  <button 
                    className={`flex items-center px-3 py-2 transition-colors whitespace-nowrap ${
                      link.menuType === 'enfants' ? 'font-bold text-base text-[#285BBB]' : 
                      link.menuType === 'formations' ? 'font-bold text-base text-[#ff3a2d]' : 'font-medium text-sm'
                    } ${
                      link.menuType !== 'enfants' && link.menuType !== 'formations' && (activeMenu === link.menuType ? 'text-[#285BBB]' : 'text-[#2C3C65] hover:text-[#285BBB]')
                    }`}
                    data-testid={`${link.menuType}-menu`}
                    onClick={() => setActiveMenu(activeMenu === link.menuType ? null : link.menuType)}
                  >
                    {link.name}
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeMenu === link.menuType ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* MEGAMENU */}
                  <AnimatePresence>
                    {activeMenu === link.menuType && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className={`fixed ${link.menuType === 'enfants' ? 'w-[900px]' : 'w-[700px]'} max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50`}
                        style={{ 
                          top: `${menuTop}px`,
                          left: '50%', 
                          marginLeft: link.menuType === 'enfants' ? '-450px' : '-350px'
                        }}
                        onMouseEnter={() => openMenu(link.menuType)}
                        onMouseLeave={closeMenuWithDelay}
                      >
                        {/* Header du megamenu */}
                        <div className="bg-gradient-to-r from-[#285BBB] to-[#2C3C65] px-6 py-4">
                          <h3 className="text-white font-bold text-lg">
                            {link.menuType === 'enfants' ? 'Cours d\'anglais - Enfants & Adolescents' : 'Nos Formations - CPF & Entreprises'}
                          </h3>
                          <p className="text-blue-100 text-sm">
                            {link.menuType === 'enfants' 
                              ? 'Certifications Cambridge English pour les jeunes' 
                              : 'Formations professionnelles certifiantes'}
                          </p>
                        </div>
                        
                        {/* Contenu du megamenu - 3 colonnes pour enfants, 2 pour formations */}
                        <div className={`grid ${link.menuType === 'enfants' ? 'grid-cols-3' : 'grid-cols-2'} gap-0 divide-x divide-gray-100`}>
                          {(link.menuType === 'enfants' ? coursAnglaisData : formationsData).map((category, idx) => {
                            const IconComponent = category.icon;
                            return (
                              <div key={idx} className="p-5 hover:bg-gray-50 transition-colors group">
                                {/* Header de catégorie */}
                                <Link 
                                  to={category.link} 
                                  onClick={() => setActiveMenu(null)}
                                  className="block mb-4"
                                >
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className={`w-10 h-10 ${category.color} rounded-xl flex items-center justify-center`}>
                                      <IconComponent className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="font-bold text-[#2C3C65] group-hover:text-[#285BBB] transition-colors">
                                        {category.category}
                                      </h4>
                                      <span className={`text-xs px-2 py-0.5 ${category.lightColor} ${category.color.replace('bg-', 'text-')} rounded-full font-medium`}>
                                        {category.subtitle}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500">{category.description}</p>
                                </Link>
                                
                                {/* Image */}
                                <Link 
                                  to={category.link} 
                                  onClick={() => setActiveMenu(null)}
                                  className="block mb-4"
                                >
                                  <div className="relative h-24 rounded-lg overflow-hidden">
                                    <img 
                                      src={category.image} 
                                      alt={category.category}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                  </div>
                                </Link>
                                
                                {/* Liste des programmes */}
                                <ul className="space-y-2">
                                  {category.programs.map((program, pidx) => (
                                    <li key={pidx}>
                                      <Link 
                                        to={category.link}
                                        onClick={() => setActiveMenu(null)}
                                        className="flex items-center justify-between p-2 rounded-lg hover:bg-white transition-colors text-sm"
                                      >
                                        <div>
                                          <span className="font-medium text-[#2C3C65]">{program.name}</span>
                                          <span className="text-gray-400 text-xs ml-2">{program.desc}</span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 ${category.lightColor} ${category.color.replace('bg-', 'text-')} rounded font-medium`}>
                                          {program.level}
                                        </span>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                                
                                {/* Lien "En savoir plus" */}
                                <Link 
                                  to={category.link}
                                  onClick={() => setActiveMenu(null)}
                                  className={`mt-4 block text-center py-2 rounded-lg ${category.lightColor} ${category.color.replace('bg-', 'text-')} font-medium text-sm hover:opacity-80 transition-opacity`}
                                >
                                  {category.category === 'Entreprises' ? 'Demander un devis →' : 'En savoir plus →'}
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Footer du megamenu */}
                        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Award className="h-4 w-4 text-[#285BBB]" />
                            <span>Centre agréé Cambridge & Certifié Qualiopi</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {link.menuType === 'enfants' && (
                              <Link 
                                to="/calendrier" 
                                onClick={() => setActiveMenu(null)}
                                className="flex items-center gap-2 text-sm font-medium text-[#285BBB] hover:underline"
                                data-testid="megamenu-calendrier-link"
                              >
                                <Calendar className="h-4 w-4" />
                                Calendrier scolaire →
                              </Link>
                            )}
                            <Link 
                              to="/demande-info" 
                              onClick={() => setActiveMenu(null)}
                              className="flex items-center gap-2 text-sm font-medium text-[#285BBB] hover:underline"
                            >
                              <Info className="h-4 w-4" />
                              Demande d'informations →
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-3 py-2 font-medium transition-colors text-sm whitespace-nowrap ${
                    isActive(link.path) 
                      ? 'text-[#285BBB]' 
                      : 'text-[#2C3C65] hover:text-[#285BBB]'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Auth Buttons / User Menu */}
          <div className="hidden lg:flex items-center space-x-2">
            {user ? (
              <div className="flex items-center space-x-4">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="px-4 py-2 text-[#285BBB] font-medium hover:bg-[#F5F7FF] rounded-lg transition-colors"
                    data-testid="admin-link"
                  >
                    Administration
                  </Link>
                )}
                {user.role === 'eleve' && user.is_approved && (
                  <Link
                    to="/espace-eleve"
                    className="px-4 py-2 text-[#285BBB] font-medium hover:bg-[#F5F7FF] rounded-lg transition-colors"
                    data-testid="eleve-space-link"
                  >
                    Espace Élève
                  </Link>
                )}
                {user.role === 'parent' && (
                  <Link
                    to="/espace-apprenant"
                    className="flex items-center px-4 py-2 text-[#2C3C65] hover:text-[#285BBB] font-medium"
                    data-testid="user-space-link"
                  >
                    <User className="h-5 w-5 mr-2" />
                    {user.prenom}
                  </Link>
                )}
                <button
                  onClick={logout}
                  className="p-2 text-gray-500 hover:text-[#ff3a2d] transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-[#2C3C65] font-bold hover:text-[#285BBB] transition-colors flex items-center gap-2"
                  data-testid="login-link"
                >
                  <LogIn className="h-4 w-4" />
                  Connexion
                </Link>
                <Link
                  to="/demande-info"
                  className="px-6 py-2.5 bg-[#ff3a2d] text-white font-semibold rounded-lg hover:bg-[#e6352a] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                  data-testid="demande-info-link"
                >
                  <Info className="h-4 w-4" />
                  Demande d'infos
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu - Scrollable */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                link.isMegaMenu ? (
                  <div key={link.name}>
                    <button 
                      className="w-full flex items-center justify-between px-4 py-3 text-[#2C3C65] font-medium"
                      onClick={() => setActiveMenu(activeMenu === link.menuType ? null : link.menuType)}
                    >
                      <span>{link.name}</span>
                      <ChevronDown className={`h-5 w-5 transition-transform ${activeMenu === link.menuType ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeMenu === link.menuType && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-gray-50 rounded-xl mx-2 overflow-hidden"
                        >
                          {(link.menuType === 'enfants' ? coursAnglaisData : formationsData).map((category, idx) => {
                            const IconComponent = category.icon;
                            return (
                              <div key={idx} className="border-b border-gray-100 last:border-0">
                                <Link
                                  to={category.link}
                                  className="flex items-center gap-3 px-4 py-3"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center`}>
                                    <IconComponent className="h-5 w-5 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-[#2C3C65]">{category.category}</div>
                                    <div className="text-xs text-gray-500">{category.subtitle}</div>
                                  </div>
                                  <ChevronDown className="h-4 w-4 text-gray-400 -rotate-90" />
                                </Link>
                                {/* Sub-programs */}
                                <div className="px-4 pb-3 flex flex-wrap gap-2">
                                  {category.programs.map((prog, pidx) => (
                                    <span 
                                      key={pidx}
                                      className={`text-xs px-2 py-1 ${category.lightColor} ${category.color.replace('bg-', 'text-')} rounded-full`}
                                    >
                                      {prog.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {link.menuType === 'enfants' && (
                            <Link
                              to="/calendrier"
                              onClick={() => { setIsMenuOpen(false); setActiveMenu(null); }}
                              className="flex items-center gap-3 px-4 py-3 bg-blue-50/50"
                              data-testid="mobile-megamenu-calendrier-link"
                            >
                              <div className="w-10 h-10 bg-[#285BBB] rounded-lg flex items-center justify-center">
                                <Calendar className="h-5 w-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-[#2C3C65]">Calendrier scolaire</div>
                                <div className="text-xs text-gray-500">Vacances & jours fériés Zone B</div>
                              </div>
                              <ChevronDown className="h-4 w-4 text-gray-400 -rotate-90" />
                            </Link>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="block px-4 py-3 text-[#2C3C65] font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              <div className="pt-4 border-t space-y-2">
                {user ? (
                  <>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-4 py-3 text-[#285BBB] font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Administration
                      </Link>
                    )}
                    {user.role === 'eleve' && user.is_approved && (
                      <Link
                        to="/espace-eleve"
                        className="block px-4 py-3 text-[#285BBB] font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Espace Élève
                      </Link>
                    )}
                    {user.role === 'parent' && (
                      <Link
                        to="/espace-apprenant"
                        className="block px-4 py-3 text-[#2C3C65] font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Espace Apprenant
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setIsMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 text-[#ff3a2d] font-medium"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="flex items-center gap-2 px-4 py-3 text-[#2C3C65] font-bold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LogIn className="h-4 w-4" />
                      Connexion
                    </Link>
                    <Link
                      to="/demande-info"
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-[#ff3a2d] text-white font-semibold rounded-lg text-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Info className="h-4 w-4" />
                      Demande d'infos
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
